import React from 'react';
import { Box, Button, Icon, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import { useInvertedLocalize } from '../../hooks';

import './AuditLogViewerError.css';

export interface AuditLogViewerErrorProps {
  className?: string;
  title?: string;
  description?: string;
  onRetry?: () => void;
  retryButtonText?: string;
}

/**
 * AuditLogViewerError Component
 *
 * Reusable error state component that displays an error icon, title, description,
 * and optional retry button.
 */
function AuditLogViewerError({
  className,
  title,
  description,
  onRetry,
  retryButtonText,
}: AuditLogViewerErrorProps): React.ReactElement {
  const l = useInvertedLocalize();

  const displayTitle =
    title ?? l('Something went wrong while loading the page.');
  const displayDescription =
    description ??
    l(
      'Try refreshing, or check back later. If the issue persists, contact your administrator.'
    );
  const displayRetry = retryButtonText ?? l('Retry');

  return (
    <Box className={cx('Aag-AuditLogViewerError', className)}>
      <Box className='Aag-AuditLogViewerError-content'>
        <Icon
          kind='StatusError'
          size='large'
          className='Aag-AuditLogViewerError-icon'
        />
        <Typography kind='h4' className='Aag-AuditLogViewerError-title'>
          {displayTitle}
        </Typography>
        <Typography
          kind='body2'
          className='Aag-AuditLogViewerError-description'>
          {displayDescription}
        </Typography>
        {onRetry && (
          <Box className='Aag-AuditLogViewerError-actions'>
            <Button kind='filled' size='medium' onClick={onRetry}>
              {displayRetry}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}

AuditLogViewerError.defaultProps = {
  className: undefined,
  title: undefined,
  description: undefined,
  onRetry: undefined,
  retryButtonText: undefined,
};

export default AuditLogViewerError;
